import { NgTemplateOutlet } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { format, parse } from 'date-fns';
import { Subject, takeUntil } from 'rxjs';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent, RktFormFieldConfig } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';

import {
  Address,
  BeneficiaryRequestParams,
  BusinessAccountAddressesData,
  FormModel,
  VerificationStatus,
  Beneficiary,
} from '@shared/models';
import { BusinessAccountService, NotificationService } from '@shared/services';
import { ErrorUtils, formatSocialSecurityNumber, phoneNumberFormat } from '@shared/utils';

import { editFormFields, editOwningFormFields } from './beneficiary-form-fields';

import { AddressAddComponent, AddressEditComponent } from '@shared/components/address';
import { ExpandableFormSectionComponent } from '@shared/components/expandable-form-section/expandable-form-section.component';
import { some } from 'lodash-es';
import { MESSAGE } from '@shared/constants';

interface BeneficiaryForm {
  firstName: string;
  middleName: string;
  lastName: string;
  socialIdCountryCode: string;
  primaryEmail: string;
  primaryPhoneNumber: string;
  dateOfBirth: string;
  cityOfBirth: string;
  countryOfBirth: string;
  title: string;
  sex: string;
  controller: boolean;
  socialId: string;
  ownershipPercentage: string;
  maskedSocialId: string;
  citizenshipCountryCode: string;
  verificationStatus?: VerificationStatus;
}

@Component({
  selector: 'app-beneficiary-edit-form',
  templateUrl: './beneficiary-edit-form.component.html',
  styleUrls: ['./beneficiary-edit-form.component.scss'],
  imports: [
    RktButtonDirective,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    RktFormComponent,
    AddressAddComponent,
    AddressEditComponent,
    ExpandableFormSectionComponent,
    RktIconComponent,
  ],
  standalone: true,
})
export class BeneficiaryEditFormComponent implements AfterViewChecked, OnDestroy {
  holderId!: string;

  isEdit = false;

  beneficiaryId?: string;

  businessAccountId?: string;

  editFormModel: FormModel<BeneficiaryForm> = {};

  editForm = new FormGroup({});

  editOwningForm = new FormGroup({});

  editFormFields: RktFormFieldConfig[] = editFormFields;

  editOwningFormFields: RktFormFieldConfig[] = editOwningFormFields();

  submitError = '';

  loading = false;

  beneficiaryAddresses: BusinessAccountAddressesData = {};

  sectionsExpanded: Record<string, boolean> = {
    beneficiaryInfo: false,
    physicalAddress: false,
    mailingAddress: false,
    shippingAddress: false,
  };

  addressFormValidity: Record<string, boolean> = {
    PHYSICAL: false,
    MAILING: false,
    SHIPPING: false,
  };

  get isSomeAddressFormInvalid(): boolean {
    return some(this.addressFormValidity, (isValid: boolean) => !isValid);
  }

  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: ActiveModal,
    private ref: ChangeDetectorRef,
    private businessAccountService: BusinessAccountService,
    private notificationService: NotificationService,
  ) {}

  ngAfterViewChecked() {
    this.ref.detectChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  modalInitData({
    beneficiaryDetails,
    businessAccountId,
    isEdit,
  }: {
    beneficiaryDetails?: Beneficiary;
    businessAccountId?: string;
    isEdit?: boolean;
  }): void {
    if (beneficiaryDetails) {
      this.isEdit = isEdit ?? false;
      this.beneficiaryId = beneficiaryDetails.id;
      this.businessAccountId = businessAccountId;

      this.editFormModel = {
        firstName: beneficiaryDetails.firstName,
        middleName: beneficiaryDetails.middleName,
        lastName: beneficiaryDetails.lastName,
        primaryEmail: beneficiaryDetails.primaryEmail?.value,
        primaryPhoneNumber: beneficiaryDetails.primaryPhoneNumber?.number,
        dateOfBirth: beneficiaryDetails.dateOfBirth
          ? format(parse(beneficiaryDetails.dateOfBirth, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')
          : undefined,
        cityOfBirth: beneficiaryDetails.placeOfBirth?.city,
        countryOfBirth: beneficiaryDetails.placeOfBirth?.countryCode,
        title: beneficiaryDetails.title,
        sex: beneficiaryDetails.sex,
        controller: beneficiaryDetails.controller,
        socialId: beneficiaryDetails.socialId,
        socialIdCountryCode: beneficiaryDetails.socialIdCountryCode,
        ownershipPercentage: beneficiaryDetails.ownershipPercentage,
        citizenshipCountryCode: beneficiaryDetails.citizenshipCountryCode,
        verificationStatus: beneficiaryDetails.verificationStatus,
      };

      if (beneficiaryDetails.addresses) {
        const physicalAddresses = beneficiaryDetails.addresses.filter((a) => a.type === 'PHYSICAL');
        const mailingAddresses = beneficiaryDetails.addresses.filter((a) => a.type === 'MAILING');
        const shippingAddresses = beneficiaryDetails.addresses.filter((a) => a.type === 'SHIPPING');

        this.beneficiaryAddresses = {
          physicalAddresses,
          mailingAddresses,
          shippingAddresses,
        };
      }
    }
  }

  onSave() {
    const {
      firstName,
      middleName,
      lastName,
      primaryEmail,
      primaryPhoneNumber,
      dateOfBirth,
      cityOfBirth,
      countryOfBirth,
      title,
      sex,
      controller,
      socialId,
      socialIdCountryCode,
      ownershipPercentage,
      citizenshipCountryCode,
    } = this.editFormModel;

    const { physicalAddresses = [], mailingAddresses = [], shippingAddresses = [] } = this.beneficiaryAddresses;

    const formattedDOB = dateOfBirth ? format(parse(dateOfBirth!, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd') : undefined;

    const beneficiaryData: BeneficiaryRequestParams = {
      firstName: firstName!,
      middleName: middleName ?? undefined,
      lastName: lastName!,
      primaryEmail: primaryEmail ? { value: primaryEmail! } : undefined,
      primaryPhoneNumber: primaryPhoneNumber ? { number: phoneNumberFormat(primaryPhoneNumber)! } : undefined,
      dateOfBirth: formattedDOB,
      placeOfBirth: cityOfBirth || countryOfBirth ? { city: cityOfBirth, countryCode: countryOfBirth } : undefined,
      title: title ?? undefined,
      sex,
      citizenshipCountryCode,
      controller: controller!,
      socialId: formatSocialSecurityNumber(socialId)!,
      socialIdCountryCode: socialIdCountryCode!,
      ownershipPercentage: ownershipPercentage ? parseFloat(ownershipPercentage) : undefined,
      addresses: [...physicalAddresses, ...mailingAddresses, ...shippingAddresses],
    };

    if (this.isEdit) {
      this.updateBusinessBeneficiary(beneficiaryData);
    } else {
      this.createBusinessBeneficiary(beneficiaryData);
    }
  }

  toggleSection(section: string): void {
    this.sectionsExpanded[section] = !this.sectionsExpanded[section];
  }

  setAddresses(addresses: Address[], addressType: keyof BusinessAccountAddressesData): void {
    this.beneficiaryAddresses[addressType] = addresses;
  }

  createBusinessBeneficiary(beneficiaryData: BeneficiaryRequestParams) {
    this.loading = true;
    this.businessAccountService
      .addBeneficiary(this.holderId, beneficiaryData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.activeModal.close(true);
          this.notificationService.displaySuccess(`Beneficial Owner created successfully.`, 'Success');
        },
        error: (error) => {
          this.submitError = typeof error === 'string' ? error : MESSAGE.GENERIC_ERROR;
          ErrorUtils.catchError('businessAccountService.addBeneficiary error', error);
          this.loading = false;
        },
      });
  }

  updateBusinessBeneficiary(beneficiaryData: BeneficiaryRequestParams) {
    this.loading = true;
    this.businessAccountService
      .updateBeneficiary(this.businessAccountId!, this.beneficiaryId!, beneficiaryData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.activeModal.close(true);
          this.notificationService.displaySuccess('Beneficial Owner updated successfully.', 'Success');
        },
        error: (error) => {
          this.submitError = typeof error === 'string' ? error : MESSAGE.GENERIC_ERROR;
          ErrorUtils.catchError('businessAccountService.updateBeneficiary error', error);
          this.loading = false;
        },
      });
  }
}
